import type { EnrollmentAutomationDay, EnrollmentAutomationStatus } from '@dev-console/enrollment-automations';
import { Pod } from '@shared/pods/constants';
import {
  deriveEnrollmentAutomationEnabled,
  deriveEnrollmentAutomationShadowMode,
  type EnrollmentAutomationFamily,
} from '@shared/provider-enrollments';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ModalShell } from '@/components/modals/modal-shell';
import { ErrorBanner, LoadingState } from '@/components/ui/state-views';
import { getEnrollmentAutomations, updateEnrollmentAutomation } from '@/config/api-helpers';
import { useAuth } from '@/lib/auth-context';

export const meta = {
  label: 'Enrollment Automations',
  order: 8,
};

const INCIDENT_LEAD_GROUP = 'SG-Eng Incident Lead';

function canEditAutomations(user: { pods?: string[]; groups?: string[] } | null): boolean {
  if (!user) return false;
  return (user.pods ?? []).includes(Pod.OPS) || (user.groups ?? []).includes(INCIDENT_LEAD_GROUP);
}

const FAMILY_SECTIONS: { family: EnrollmentAutomationFamily; label: string }[] = [
  { family: 'enrollment', label: 'Enrollment' },
  { family: 'statusCheck', label: 'Status checks' },
];

const PILL_TONES = {
  on: 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20',
  off: 'bg-neutral-100 text-neutral-500 ring-1 ring-inset ring-neutral-500/20',
  shadow: 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-600/20',
  standard: 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20',
  error: 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20',
} as const;

const BTN_PRIMARY =
  'rounded-md bg-red-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50';
const BTN_NEUTRAL =
  'rounded-md border border-neutral-200 bg-white px-4 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-50';

function Pill({ tone, children }: { tone: keyof typeof PILL_TONES; children: React.ReactNode }) {
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${PILL_TONES[tone]}`}>
      {children}
    </span>
  );
}

// Whether the given config object would cause real payer filings: enabled, and standard (or a payer
// that ignores shadow mode and so always files for real).
function willFileForReal(row: EnrollmentAutomationStatus, enabled: boolean, shadowMode: boolean): boolean {
  return enabled && (row.supportsShadowMode ? !shadowMode : true);
}

function EnabledCell({ row }: { row: EnrollmentAutomationStatus }) {
  if (row.readError != null) {
    return <span className='text-xs text-neutral-400'>—</span>;
  }
  return (
    <span className='flex items-center gap-1.5'>
      <Pill tone={row.enabled ? 'on' : 'off'}>{row.enabled ? 'on' : 'off'}</Pill>
      {!row.enabledGated && (
        <span className='text-xs text-neutral-400' title='No enabled gate on the automated path; a cron dispatches it'>
          cron
        </span>
      )}
    </span>
  );
}

function ShadowCell({ row }: { row: EnrollmentAutomationStatus }) {
  if (row.readError != null) {
    return <span className='text-xs text-neutral-400'>—</span>;
  }
  if (!row.supportsShadowMode) {
    return (
      <span className='text-xs text-neutral-400' title='This payer ignores shadow mode; dispatches always run standard'>
        n/a
      </span>
    );
  }
  return row.shadowMode ? <Pill tone='shadow'>shadow</Pill> : <Pill tone='standard'>standard</Pill>;
}

// Each day takes its worst outcome, GitHub-status-strip style. An automation failure (the workflow
// broke) outranks a validation failure (a run lacked the data to file); skipped-only and no-run days
// read grey because neither is an automation problem.
function dayTone(day: EnrollmentAutomationDay): string {
  if (day.automationFailure > 0) return 'bg-red-500';
  if (day.validationFailure > 0) return 'bg-orange-400';
  if (day.success > 0) return 'bg-green-500';
  if (day.prepared > 0) return 'bg-blue-500';
  return 'bg-neutral-200';
}

function dayTitle(day: EnrollmentAutomationDay): string {
  if (day.total === 0) {
    return `${day.date}: no runs`;
  }
  return (
    `${day.date}: success ${day.success}, prepared for review ${day.prepared ?? 0}, automation failures ${
      day.automationFailure
    }, ` + `validation failures ${day.validationFailure}, skipped ${day.skipped}, pending ${day.pending}`
  );
}

function HealthCell({ row }: { row: EnrollmentAutomationStatus }) {
  if (row.health == null) {
    return <span className='text-xs text-neutral-300'>—</span>;
  }
  const { days, success, automationFailure } = row.health;
  // Success rate reflects automation health only: a validation failure is not the automation failing.
  const rated = success + automationFailure;
  const rate = rated > 0 ? `${Math.round((success / rated) * 100)}%` : '—';
  return (
    <span className='flex items-center gap-2'>
      <span className='flex items-end gap-px'>
        {days.map((day) => (
          <span key={day.date} title={dayTitle(day)} className={`h-5 w-[5px] rounded-sm ${dayTone(day)}`} />
        ))}
      </span>
      <span className='min-w-10 text-xs tabular-nums text-neutral-500'>{rate}</span>
    </span>
  );
}

function EditAutomationModal({ row, onClose }: { row: EnrollmentAutomationStatus; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState(() => JSON.stringify(row.config ?? {}, null, 2));
  const [parseError, setParseError] = useState<string | null>(null);
  const [pendingConfig, setPendingConfig] = useState<Record<string, unknown> | null>(null);

  const mutation = useMutation({
    mutationFn: (config: Record<string, unknown>) => updateEnrollmentAutomation(row.configName, { config }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enrollment-automations'] });
      onClose();
    },
  });

  function review() {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch (error) {
      setParseError(error instanceof Error ? error.message : 'Invalid JSON');
      return;
    }
    if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setParseError('Config must be a JSON object');
      return;
    }
    setParseError(null);
    setPendingConfig(parsed as Record<string, unknown>);
  }

  if (pendingConfig != null) {
    const willEnable = deriveEnrollmentAutomationEnabled(pendingConfig);
    const willShadow = deriveEnrollmentAutomationShadowMode(row.family, pendingConfig);
    const filesForReal = willFileForReal(row, willEnable, willShadow);
    return (
      <ModalShell
        title={`Save ${row.payerLabel}?`}
        onClose={onClose}
        footer={
          <>
            <button
              type='button'
              className={BTN_NEUTRAL}
              onClick={() => setPendingConfig(null)}
              disabled={mutation.isPending}
            >
              Back
            </button>
            <button
              type='button'
              className={BTN_PRIMARY}
              onClick={() => mutation.mutate(pendingConfig)}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Saving…' : 'Confirm & save'}
            </button>
          </>
        }
      >
        <p>
          This writes <code className='font-mono text-xs'>{row.configName}</code> to Statsig.
        </p>
        <ul className='mt-2 space-y-0.5 text-xs text-neutral-600'>
          <li>enabled: {String(willEnable)}</li>
          <li>shadow: {row.supportsShadowMode ? String(willShadow) : 'n/a (ignored)'}</li>
        </ul>
        {filesForReal && (
          <p className='mt-3 rounded-md bg-red-50 px-3 py-2 text-xs font-medium text-red-700'>
            This enables REAL filings to {row.payerLabel}. The next matched provider will file an application.
          </p>
        )}
        {mutation.isError && (
          <p className='mt-3 text-xs text-red-600'>
            {mutation.error instanceof Error ? mutation.error.message : 'Save failed'}
          </p>
        )}
      </ModalShell>
    );
  }

  return (
    <ModalShell
      title={`Edit ${row.payerLabel}`}
      onClose={onClose}
      footer={
        <>
          <button type='button' className={BTN_NEUTRAL} onClick={onClose}>
            Cancel
          </button>
          <button type='button' className={BTN_PRIMARY} onClick={review}>
            Review change
          </button>
        </>
      }
    >
      <p className='mb-2 text-xs text-neutral-500'>
        The whole config default value. Edit any field; the object replaces the current value.
      </p>
      <textarea
        value={text}
        onChange={(event) => setText(event.target.value)}
        rows={14}
        spellCheck={false}
        className='w-full rounded-md border border-neutral-300 p-2 font-mono text-xs text-neutral-900'
      />
      {parseError != null && <p className='mt-2 text-xs text-red-600'>{parseError}</p>}
    </ModalShell>
  );
}

function AutomationRow({
  row,
  canEdit,
  onEdit,
}: {
  row: EnrollmentAutomationStatus;
  canEdit: boolean;
  onEdit: (row: EnrollmentAutomationStatus) => void;
}) {
  return (
    <tr className='border-b border-neutral-100'>
      <td className='px-4 py-2.5'>
        <div className='text-sm font-medium text-neutral-900'>{row.payerLabel}</div>
        <a
          href={row.statsigUrl}
          target='_blank'
          rel='noreferrer'
          className='font-mono text-xs text-blue-600 hover:underline'
        >
          {row.configName}
        </a>
      </td>
      <td className='px-4 py-2.5'>
        <EnabledCell row={row} />
      </td>
      <td className='px-4 py-2.5'>
        <ShadowCell row={row} />
      </td>
      <td className='px-4 py-2.5'>
        <HealthCell row={row} />
      </td>
      <td className='px-4 py-2.5'>
        {row.readError != null ? (
          <span className='text-xs text-red-600' title={row.readError}>
            read failed
          </span>
        ) : row.lastModifierName != null ? (
          <span className='text-xs text-neutral-400'>by {row.lastModifierName}</span>
        ) : null}
      </td>
      <td className='px-4 py-2.5 text-right'>
        {canEdit && row.readError == null && (
          <button
            type='button'
            onClick={() => onEdit(row)}
            className='rounded-md border border-neutral-200 bg-white px-2.5 py-1 text-xs text-neutral-700 hover:bg-neutral-50'
          >
            Edit
          </button>
        )}
      </td>
    </tr>
  );
}

function FamilySection({
  label,
  rows,
  canEdit,
  onEdit,
}: {
  label: string;
  rows: EnrollmentAutomationStatus[];
  canEdit: boolean;
  onEdit: (row: EnrollmentAutomationStatus) => void;
}) {
  return (
    <section className='overflow-hidden rounded-lg border border-neutral-200 bg-white'>
      <div className='flex items-baseline gap-2 border-b border-neutral-200 bg-neutral-50 px-4 py-2'>
        <h2 className='text-xs font-semibold uppercase tracking-wide text-neutral-500'>{label}</h2>
        <span className='text-xs text-neutral-400'>{rows.length}</span>
      </div>
      <table className='w-full'>
        <thead>
          <tr className='border-b border-neutral-200 text-left text-xs text-neutral-500'>
            <th className='px-4 py-2 font-medium'>Workflow</th>
            <th className='px-4 py-2 font-medium'>Enabled</th>
            <th className='px-4 py-2 font-medium'>Shadow</th>
            <th
              className='px-4 py-2 font-medium'
              title='One bar per day, oldest to newest. Red: an automation failure (the workflow broke). Orange: a validation failure (a run lacked the data to file). Green: runs passed. Grey: only skipped or no runs. % is the automation success rate over the window.'
            >
              Health · 14d
            </th>
            <th className='px-4 py-2 font-medium'>Last change</th>
            <th className='px-4 py-2 font-medium'></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <AutomationRow key={row.configName} row={row} canEdit={canEdit} onEdit={onEdit} />
          ))}
        </tbody>
      </table>
    </section>
  );
}

export function EnrollmentAutomationsPage() {
  const { user } = useAuth();
  const canEdit = canEditAutomations(user);
  const [editing, setEditing] = useState<EnrollmentAutomationStatus | null>(null);

  const { data, error, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['enrollment-automations'],
    queryFn: getEnrollmentAutomations,
    staleTime: 30_000,
  });

  return (
    <div className='space-y-4 p-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-lg font-semibold text-neutral-900'>Enrollment Automations</h1>
          <p className='text-sm text-neutral-500'>
            Live Statsig config status for every provider enrollment and payer status-check workflow.
            {data != null && <> Health shows one bar per day over the last {data.healthWindowDays} days.</>}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className='rounded-md border border-neutral-200 bg-white px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-50'
        >
          Refresh
        </button>
      </div>

      {isLoading && data == null && <LoadingState message='Reading configs from Statsig…' />}

      {error && (
        <ErrorBanner
          message={error instanceof Error ? error.message : 'Failed to load enrollment automations'}
          onRetry={() => refetch()}
        />
      )}

      {data != null &&
        FAMILY_SECTIONS.map(({ family, label }) => (
          <FamilySection
            key={family}
            label={label}
            canEdit={canEdit}
            onEdit={setEditing}
            rows={data.automations.filter((automation) => automation.family === family)}
          />
        ))}

      {editing != null && <EditAutomationModal row={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
